import {
  apiV1TeamsGet,
  apiV1TeamsMyScopeGet,
  apiV1TeamsPost,
  apiV1TeamsTeamIdAccountsPut,
  apiV1TeamsTeamIdDelete,
  apiV1TeamsTeamIdGet,
  apiV1TeamsTeamIdMembersPut,
  apiV1TeamsTeamIdOverviewGet,
  apiV1TeamsTeamIdPut,
} from "@/backend-sdk/client/sdk.gen";
import type {
  TeamAccountItem,
  TeamCreateRequest,
  TeamDetail,
  TeamItem,
  TeamMemberItem,
  TeamMessageResponse,
  TeamOverviewResponse,
  TeamOverviewStore,
  TeamOverviewTotals,
  TeamScopeAccount,
  TeamScopeItem,
  TeamUpdateRequest,
} from "@/backend-sdk/client/types.gen";
import { heyAdminApiClient } from "@/lib/hey-api-client";

/**
 * Os **times da agência** — o recorte de acesso que ela usa para trabalhar.
 *
 * ## As duas metades
 *
 * **Quem administra a agência** (o assinante, ou quem tem papel `admin`/`owner` na conta
 * principal) usa as sete chamadas de gestão: listar, criar, detalhar, atualizar, excluir e
 * os dois `PUT` de vínculo. Os dois últimos recebem o **conjunto inteiro** e são
 * idempotentes — mandar a lista com um id a menos é o que remove aquele vínculo —, e é por
 * isso que a tela envia sempre a seleção completa, nunca um delta.
 *
 * **O membro** usa duas: `getMyScope()`, que responde de que times ele faz parte e quais
 * lojas ele alcança, e `getOverview()`, o painel consolidado. As duas respondem `200` para
 * qualquer autenticado — o assinante recebe `teams: []` porque não é membro de nada —, e
 * por isso `getMyScope` é seguro de chamar em todo carregamento de perfil.
 *
 * ## Por que tudo vai pelo `heyAdminApiClient`
 *
 * O cliente padrão manda o token da subconta quando existe um. Quem administra a agência e
 * entrou numa loja passaria a falar com estas rotas **como o dono daquela loja** — que é
 * `owner` da loja e não administra os times da agência (D-059), e receberia `403`. O
 * `heyAdminApiClient` manda sempre o token principal, que é o do próprio operador. Para o
 * membro os dois clientes são o mesmo token: ele nunca troca de conta, porque não pode usar
 * `POST /accounts/switch-account` — essa rota é do assinante e emitiria uma credencial do
 * dono da loja, com acesso total.
 *
 * O cabeçalho `x-ignore-global-filters` mantém o período global fora das rotas de gestão,
 * que não têm data nenhuma a filtrar. O consolidado é a exceção, e nele o período vai
 * explícito, como em toda tela datada.
 */

export type {
  TeamAccountItem,
  TeamDetail,
  TeamItem,
  TeamMemberItem,
  TeamOverviewResponse,
  TeamOverviewStore,
  TeamOverviewTotals,
  TeamScopeAccount,
  TeamScopeItem,
};

/**
 * O que `GET /api/v1/teams/my-scope` devolve, já sem o envelope `data`.
 *
 * <M-248> As lojas daqui são `TeamScopeAccount`, e não `TeamAccountItem`: além do que a
 * agência vê da composição de um time, cada uma diz `can_write` — se a barreira aceita
 * escrita de quem perguntou. O tipo é outro de propósito, porque a pergunta é sobre quem
 * está olhando, e em `GET /teams/{team_id}` ela não faria sentido.
 */
export interface TeamScope {
  teams: TeamScopeItem[];
  accounts: TeamScopeAccount[];
}

/** O período que o painel consolidado consulta — os mesmos nomes de `analytics-overview`. */
export interface TeamOverviewParams {
  start_date: string;
  end_date: string;
  comparison_type?: string | null;
}

/** Sem filtro global: rota de gestão não tem data a filtrar. */
const semFiltroGlobal = { "x-ignore-global-filters": "1" } as const;

class TeamService {
  /** `GET /api/v1/teams` — o resumo de cada time, com as contagens que a lista mostra. */
  async list(): Promise<TeamItem[]> {
    const { data } = await apiV1TeamsGet({
      client: heyAdminApiClient,
      headers: semFiltroGlobal,
      throwOnError: true,
    });
    return data.data;
  }

  /** `POST /api/v1/teams`. Membros e lojas são opcionais: time vazio é estado legítimo. */
  async create(payload: TeamCreateRequest): Promise<TeamDetail> {
    const { data } = await apiV1TeamsPost({
      body: payload,
      client: heyAdminApiClient,
      headers: semFiltroGlobal,
      throwOnError: true,
    });
    return data.data;
  }

  /** `GET /api/v1/teams/{team_id}` — o time com a composição inteira. */
  async get(teamId: string): Promise<TeamDetail> {
    const { data } = await apiV1TeamsTeamIdGet({
      path: { team_id: teamId },
      client: heyAdminApiClient,
      headers: semFiltroGlobal,
      throwOnError: true,
    });
    return data.data;
  }

  /**
   * `PUT /api/v1/teams/{team_id}`.
   *
   * `description: null` **apaga** a descrição — aqui `null` quer dizer o que parece querer
   * dizer, ao contrário do `PUT` de Minha Conta. `is_active: false` desativa o time, e o
   * alcance dos membros some na requisição seguinte.
   */
  async update(
    teamId: string,
    payload: TeamUpdateRequest,
  ): Promise<TeamDetail> {
    const { data } = await apiV1TeamsTeamIdPut({
      path: { team_id: teamId },
      body: payload,
      client: heyAdminApiClient,
      headers: semFiltroGlobal,
      throwOnError: true,
    });
    return data.data;
  }

  async remove(teamId: string): Promise<TeamMessageResponse> {
    const { data } = await apiV1TeamsTeamIdDelete({
      path: { team_id: teamId },
      client: heyAdminApiClient,
      headers: semFiltroGlobal,
      throwOnError: true,
    });
    return data;
  }

  /**
   * `PUT /api/v1/teams/{team_id}/members` — o conjunto **inteiro** de membros.
   *
   * Quem já estava no time preserva o `joined_at` que a tela mostra; quem não vier na lista
   * sai. Ids repetidos não são erro: o contrato os deduplica.
   */
  async setMembers(teamId: string, userIds: string[]): Promise<TeamDetail> {
    const { data } = await apiV1TeamsTeamIdMembersPut({
      path: { team_id: teamId },
      body: { user_ids: userIds },
      client: heyAdminApiClient,
      headers: semFiltroGlobal,
      throwOnError: true,
    });
    return data.data;
  }

  /** `PUT /api/v1/teams/{team_id}/accounts` — o conjunto **inteiro** de lojas. */
  async setAccounts(teamId: string, accountIds: string[]): Promise<TeamDetail> {
    const { data } = await apiV1TeamsTeamIdAccountsPut({
      path: { team_id: teamId },
      body: { account_ids: accountIds },
      client: heyAdminApiClient,
      headers: semFiltroGlobal,
      throwOnError: true,
    });
    return data.data;
  }

  /**
   * `GET /api/v1/teams/my-scope` — de que times eu faço parte, e que lojas alcanço.
   *
   * É a **única** fonte das lojas do membro: `GET /api/v1/accounts` responde
   * `403 Usuário não é um assinante` para ele. As lojas vêm deduplicadas entre times e só
   * de times ativos — o mesmo conjunto que a barreira de tenancy aceita, montado pela mesma
   * origem, para que a tela nunca ofereça uma loja que a API vá recusar.
   */
  async getMyScope(): Promise<TeamScope> {
    const { data } = await apiV1TeamsMyScopeGet({
      client: heyAdminApiClient,
      headers: semFiltroGlobal,
      throwOnError: true,
    });
    return data.data;
  }

  /**
   * `GET /api/v1/teams/{team_id}/overview` — os números das lojas do time somados.
   *
   * Quem pode: o membro e quem administra a agência. Não há consulta consolidada do lado
   * dos provedores; o servidor faz fan-out por loja, no máximo 50 por requisição, e as que
   * passam do teto voltam com `status: 'nao_consultada'`.
   */
  async getOverview(
    teamId: string,
    params: TeamOverviewParams,
  ): Promise<TeamOverviewResponse["data"]> {
    const { data } = await apiV1TeamsTeamIdOverviewGet({
      path: { team_id: teamId },
      query: {
        start_date: params.start_date,
        end_date: params.end_date,
        comparison_type: params.comparison_type ?? undefined,
      },
      client: heyAdminApiClient,
      headers: semFiltroGlobal,
      throwOnError: true,
    });
    return data.data;
  }
}

export const teamService = new TeamService();
export default teamService;
